import { getSlateEmptyValue } from 'components/TemplateEditor/utils/slateNodeHelper';
import ExecuteContext from '../ExecuteContext';
import { Button } from 'antd';
import type { ExecuteContextsFieldV2Props } from '../ExecuteContextsFieldV2';
import { RecommendTemplates, RecommendTemplates2 } from '../RecommendTemplates';
import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { Descendant } from 'slate';
import { useMemo, useState } from 'react';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { FileElement } from 'components/TemplateEditor/slateTypes';
import { v4 } from 'uuid';
import { useTranslate } from '@tolgee/react';
import useStore from 'views/Setting/Phrase/hooks/useStore';
import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import { DescendantExecuteContext } from '../share';
import { AttachmentUploadActionComponent } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/AttachmentUpload/AttachmentUploadAction';
import { useUploadResource } from 'hooks/business/useUploadResource';
import { AttachmentUploadComponent } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/AttachmentUpload';

interface Attachment {
  id: string;
  status: 'uploading' | 'success' | 'error';
  data: Conversation.MsgAttachmentInfo;
}

function MercadoExecuteContexts(props: ExecuteContextsFieldV2Props) {
  const {
    scenarioType,
    platform,
    region,
    value,
    onChange,
    supportTranslate,
    replyOption,
    extraConcepts,
    langTemplatesMap,
    recommendTemplates,
    sendPoint,
    disabled,
  } = props;

  const { t } = useTranslate();

  const [attachmentList, setAttachmentList] = useState<Attachment[]>(() => {
    if (!value) {
      return [];
    }

    let fileList = value.translatedContent.filter(
      (_) => _.type === CustomSlateNodeType.FILE,
    ) as FileElement[];
    if (fileList.length === 0) {
      fileList = value.content.filter((_) => _.type === CustomSlateNodeType.FILE) as FileElement[];
    }

    return fileList.map(({ data }) => {
      return {
        id: v4(),
        status: 'success',
        data,
      };
    });
  });

  const executeContext = useMemo(() => {
    if (!value) {
      return {
        content: getSlateEmptyValue(),
        translatedContent: getSlateEmptyValue(),
      };
    }

    return {
      content: value.content.filter((_) => _.type !== CustomSlateNodeType.FILE),
      translatedContent: value.translatedContent.filter((_) => _.type !== CustomSlateNodeType.FILE),
    };
  }, [value]);

  const onChangeWithAttachment = useMemoizedFn((executeContext: DescendantExecuteContext) => {
    const validList = attachmentList
      .filter((_) => _.status === 'success')
      .map(({ data }) => ({
        type: CustomSlateNodeType.FILE as const,
        data,
      }));

    onChange({
      content: [...executeContext.content, ...validList],
      translatedContent: getSegmentsLength(executeContext.translatedContent)
        ? [...executeContext.translatedContent, ...validList]
        : executeContext.translatedContent,
    });
  });

  useUpdateEffect(() => {
    onChangeWithAttachment(executeContext);
  }, [attachmentList]);

  const onTemplateSelect = useMemoizedFn((content: Descendant[]) =>
    onChange({
      content,
      translatedContent: getSlateEmptyValue(),
    }),
  );

  const store = useStore('MERCADO', region);

  const uploadResource = useUploadResource();

  const onUpload = useMemoizedFn((file: File) => {
    const uuid = v4();

    setAttachmentList((pre) => [
      ...pre,
      {
        id: uuid,
        status: 'uploading',
        data: {
          filename: '',
          originalFilename: file.name,
          size: `${file.size}`,
          type: file.type,
          url: '',
        },
      },
    ]);

    uploadResource({
      file,
      storeId: `${store?.id}`,
      region: store?.region,
      fileType: 'IMAGE',
      reqSource: 'ATTACHMENT',
    })
      .then(({ resourceId, url, localUrl }) => {
        if (!localUrl) {
          return Promise.reject(new Error('no localUrl'));
        }

        setAttachmentList((list) =>
          list.map((attachment) => {
            if (attachment.id !== uuid) {
              return attachment;
            }
            return {
              id: uuid,
              status: 'success',
              data: {
                ...attachment.data,
                filename: supportTranslate ? url : resourceId,
                url: localUrl,
              },
            };
          }),
        );
      })
      .catch(() => {
        setAttachmentList((list) =>
          list.map((attachment) => {
            if (attachment.id !== uuid) {
              return attachment;
            }
            return {
              ...attachment,
              status: 'error',
            };
          }),
        );
      });
  });

  const uploadAction = store && (
    <AttachmentUploadActionComponent
      channel="MERCADO"
      attachmentList={attachmentList}
      onAttachmentUpload={onUpload}
    >
      <Button disabled={disabled} type="link">
        {t('添加附件')}
      </Button>
    </AttachmentUploadActionComponent>
  );

  const uploadResult = useMemo(() => {
    return attachmentList.length === 0 ? null : (
      <AttachmentUploadComponent
        className="!rounded"
        attachmentList={attachmentList}
        onRemove={(id) => setAttachmentList((list) => list.filter((_) => _.id !== id))}
        getPopupContainer={(trigger) => trigger}
      />
    );
  }, [attachmentList]);

  return (
    <div>
      <ExecuteContext
        disabled={disabled}
        scenarioType={scenarioType}
        platform={platform}
        region={region}
        allowDividing={false}
        executeContext={executeContext}
        onExecuteContextChange={onChangeWithAttachment}
        leftExtra={uploadAction}
        attachment={uploadResult}
        supportTranslate={supportTranslate}
        replyOption={replyOption}
        extraConcepts={extraConcepts}
      />
      {!langTemplatesMap && (
        <RecommendTemplates
          region={region}
          recommendTemplates={recommendTemplates}
          onTemplateSelect={onTemplateSelect}
        />
      )}
      {sendPoint !== 'IN_CANCEL' && (
        <RecommendTemplates2
          region={region}
          langTemplatesMap={langTemplatesMap}
          onTemplateSelect={onTemplateSelect}
        />
      )}
    </div>
  );
}

export default MercadoExecuteContexts;
